import { useEffect, useMemo, useRef, useState } from 'react';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/Grid';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import {
  createPeripheral,
  PeripheralResponse,
  revalidatePeripherals,
  updatePeripheral,
  updatePeripheralStatus,
  useGetPeripheralCategories,
  useGetPeripheralsBySite,
  useGetPeripheralTypes
} from 'api/peripherals';
import useTenantSelection from 'hooks/useTenantSelection';
import LFDPeripheralConfig, {
  buildDefaultLfdConfig,
  LfdConfig,
  LfdIndicatorOption,
  readIndicatorPlatforms
} from './peripherals/LFDPeripheralConfig';
import WeighbridgePeripheralConfig, {
  buildDefaultWeighbridgeConfig,
  WeighbridgeConfig,
  WeighbridgePresetTemplate
} from './peripherals/WeighbridgePeripheralConfig';
import LoaderScalePeripheralConfig, { toLoaderScaleConfig } from './peripherals/LoaderScalePeripheralConfig';

import ANPRPeripheralConfig, { AnprConfig, buildDefaultAnprConfig } from './peripherals/ANPRPeripheralConfig';
import LinkedDevicesConfig, { LinkedDevice, linkedDevicesToJson, parseLinkedDevices } from './peripherals/LinkedDevicesConfig';
import RelayPeripheralConfig, { buildDefaultRelayConfig, RelayConfig, relayConfigToJson } from './peripherals/RelayPeripheralConfig';

export default function PeripheralManagement() {
  const { selectedCompanySiteResult } = useTenantSelection();

  const { peripheralCategories, peripheralCategoriesLoading, peripheralCategoriesError } = useGetPeripheralCategories();
  const { peripheralTypes, peripheralTypesLoading, peripheralTypesError } = useGetPeripheralTypes();

  const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(null);
  const [selectedPeripheralTypeId, setSelectedPeripheralTypeId] = useState<number | null>(null);
  const [selectedPeripheralId, setSelectedPeripheralId] = useState<string | null>(null);
  const [newTypeName, setNewTypeName] = useState('');
  const [locationInput, setLocationInput] = useState('');
  const [notesInput, setNotesInput] = useState('');
  const [siteIdInput, setSiteIdInput] = useState<string>('');
  const [weighbridgeConfig, setWeighbridgeConfig] = useState<WeighbridgeConfig>(() => buildDefaultWeighbridgeConfig());
  const [lfdConfig, setLfdConfig] = useState<LfdConfig>(() => buildDefaultLfdConfig());
  const [anprConfig, setAnprConfig] = useState<AnprConfig>(() => buildDefaultAnprConfig());
  // Barrier arms and traffic lights share the relay editor.
  const [relayConfig, setRelayConfig] = useState<RelayConfig>(() => buildDefaultRelayConfig());
  // Linked devices for categories without their own configuration editor, eg. dallas tag readers.
  const [otherLinkedDevices, setOtherLinkedDevices] = useState<LinkedDevice[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [peripheralActiveFilter, setPeripheralActiveFilter] = useState<'active' | 'inactive' | 'all'>('active');
  const pendingSelectedConfigRef = useRef<unknown | null>(null);

  const selectedSiteId = useMemo(() => {
    const parsed = Number(siteIdInput);
    return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
  }, [siteIdInput]);

  const peripheralActiveParam = peripheralActiveFilter === 'active' ? true : peripheralActiveFilter === 'inactive' ? false : null;

  const { peripherals, peripheralsLoading, peripheralsError, revalidatePeripheralsBySite } = useGetPeripheralsBySite(
    selectedSiteId,
    peripheralActiveParam
  );

  const activeCategories = useMemo(
    () => peripheralCategories.filter((category) => category.isActive).sort((a, b) => a.dispOrder - b.dispOrder),
    [peripheralCategories]
  );

  useEffect(() => {
    if (activeCategories.length === 0) {
      setSelectedCategoryId(null);
      return;
    }

    setSelectedCategoryId((current) => {
      if (current != null && activeCategories.some((category) => category.peripheralCategoryId === current)) {
        return current;
      }

      return activeCategories[0].peripheralCategoryId;
    });
  }, [activeCategories]);

  const selectedCategory = useMemo(() => {
    if (selectedCategoryId == null) {
      return null;
    }

    return activeCategories.find((category) => category.peripheralCategoryId === selectedCategoryId) ?? null;
  }, [activeCategories, selectedCategoryId]);

  const peripheralTypeById = useMemo(() => {
    return new Map(peripheralTypes.map((type) => [type.peripheralTypeId, type]));
  }, [peripheralTypes]);

  const categoryPeripherals = useMemo(() => {
    if (selectedCategoryId == null) {
      return [];
    }
    //console.log('peripherals', peripherals, 'selectedCategoryId', selectedCategoryId);
    return peripherals
      .filter((peripheral) => {
        const type = peripheralTypeById.get(peripheral.peripheralTypeId);
        return type?.peripheralCategoryId === selectedCategoryId;
      })
      .sort((a, b) => a.peripheralName.localeCompare(b.peripheralName));
  }, [peripherals, peripheralTypeById, selectedCategoryId]);

  // Read separately from the list above, which follows the active/inactive filter: an LFD can only be
  // linked to an indicator that is running.
  const { peripherals: activeSitePeripherals } = useGetPeripheralsBySite(selectedSiteId, true);

  // The indicators an LFD can be linked to: the site's active weighbridge indicators, with the platforms
  // (scales) each is configured with.
  const lfdIndicatorOptions = useMemo<LfdIndicatorOption[]>(() => {
    const indicatorCategoryIds = new Set(
      activeCategories
        .filter((category) => category.peripheralCategoryName.trim().toLowerCase() === 'weighbridge indicator')
        .map((category) => category.peripheralCategoryId)
    );

    return activeSitePeripherals
      .filter(
        (peripheral) =>
          peripheral.peripheralId != null &&
          indicatorCategoryIds.has(peripheralTypeById.get(peripheral.peripheralTypeId)?.peripheralCategoryId ?? -1)
      )
      .map((peripheral) => ({
        peripheralId: peripheral.peripheralId!,
        name: peripheral.peripheralName,
        platforms: readIndicatorPlatforms(peripheral.configuration)
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [activeSitePeripherals, activeCategories, peripheralTypeById]);

  const selectedCategoryName = (selectedCategory?.peripheralCategoryName ?? '').trim().toLowerCase();
  const isWeighbridgeCategory = selectedCategoryName === 'weighbridge indicator';
  // Saved in the same shape as a weighbridge indicator, so it shares that form state.
  const isLoaderScaleCategory = selectedCategoryName === 'loader scale';
  const isLfdCategory = selectedCategoryName === 'lfd';
  const isANPRCategory = selectedCategoryName === 'anpr camera';
  const isBarrierArmCategory = selectedCategoryName === 'barrier arm';
  const isTrafficLightCategory = selectedCategoryName === 'traffic light';
  const isRelayCategory = isBarrierArmCategory || isTrafficLightCategory;

  const peripheralTypeOptions = useMemo(() => {
    if (selectedCategoryId == null) {
      return [];
    }

    return peripheralTypes
      .filter((type) => type.isActive && type.peripheralCategoryId === selectedCategoryId)
      .sort((a, b) => a.peripheralTypeName.localeCompare(b.peripheralTypeName));
  }, [peripheralTypes, selectedCategoryId]);

  const selectedPeripheralType = useMemo(() => {
    if (selectedPeripheralTypeId == null) {
      return null;
    }

    return peripheralTypeOptions.find((type) => type.peripheralTypeId === selectedPeripheralTypeId) ?? null;
  }, [peripheralTypeOptions, selectedPeripheralTypeId]);

  const selectedTypeDefaultConfiguration = useMemo(() => {
    const raw = selectedPeripheralType?.defaultConfiguration;
    if (!raw) {
      return null;
    }

    if (typeof raw !== 'string') {
      return raw;
    }

    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }, [selectedPeripheralType]);

  const weighbridgeTemplates = useMemo<WeighbridgePresetTemplate[]>(() => {
    const templates: WeighbridgePresetTemplate[] = [];

    peripheralTypeOptions.forEach((type) => {
      if (!type.defaultConfiguration) {
        return;
      }

      let parsed: unknown = null;
      if (typeof type.defaultConfiguration === 'string') {
        try {
          parsed = JSON.parse(type.defaultConfiguration);
        } catch {
          parsed = null;
        }
      } else {
        parsed = type.defaultConfiguration;
      }

      if (!parsed) {
        return;
      }

      templates.push({
        id: type.peripheralTypeId,
        label: type.peripheralTypeName,
        configuration: parsed
      });
    });

    return templates;
  }, [peripheralTypeOptions]);

  const loading = peripheralCategoriesLoading || peripheralTypesLoading;
  const loadError =
    (typeof peripheralCategoriesError?.message === 'string' && peripheralCategoriesError.message) ||
    (typeof peripheralTypesError?.message === 'string' && peripheralTypesError.message) ||
    null;

  useEffect(() => {
    if (peripheralTypeOptions.length === 0) {
      setSelectedPeripheralTypeId(null);
      return;
    }

    setSelectedPeripheralTypeId((current) => {
      if (current != null && peripheralTypeOptions.some((type) => type.peripheralTypeId === current)) {
        return current;
      }

      return peripheralTypeOptions[0].peripheralTypeId;
    });
  }, [peripheralTypeOptions]);

  useEffect(() => {
    if (pendingSelectedConfigRef.current != null) {
      setWeighbridgeConfig(buildDefaultWeighbridgeConfig(pendingSelectedConfigRef.current));
      setLfdConfig(buildDefaultLfdConfig(pendingSelectedConfigRef.current));
      setAnprConfig(buildDefaultAnprConfig(pendingSelectedConfigRef.current));
      setRelayConfig(buildDefaultRelayConfig(pendingSelectedConfigRef.current));
      setOtherLinkedDevices(parseLinkedDevices(pendingSelectedConfigRef.current));
      pendingSelectedConfigRef.current = null;
      return;
    }

    setWeighbridgeConfig(buildDefaultWeighbridgeConfig(selectedTypeDefaultConfiguration));
    setLfdConfig(buildDefaultLfdConfig(selectedTypeDefaultConfiguration));
    setAnprConfig(buildDefaultAnprConfig(selectedTypeDefaultConfiguration));
    setRelayConfig(buildDefaultRelayConfig(selectedTypeDefaultConfiguration));
    setOtherLinkedDevices(parseLinkedDevices(selectedTypeDefaultConfiguration));
  }, [selectedTypeDefaultConfiguration]);

  useEffect(() => {
    setAnprConfig((prev) => ({ ...prev, name: newTypeName }));
  }, [newTypeName, locationInput]);

  useEffect(() => {
    if (selectedPeripheralId == null) {
      return;
    }

    const stillExists = categoryPeripherals.some((peripheral) => peripheral.peripheralId === selectedPeripheralId);
    if (!stillExists) {
      setSelectedPeripheralId(null);
    }
  }, [categoryPeripherals, selectedPeripheralId]);

  useEffect(() => {
    const siteId = selectedCompanySiteResult?.siteId;
    if (siteId == null) {
      return;
    }

    setSiteIdInput(String(siteId));
  }, [selectedCompanySiteResult?.siteId]);

  const handleSavePeripheral = async () => {
    if (selectedCategoryId == null || isSaving) {
      return;
    }

    if (selectedPeripheralTypeId == null) {
      setError('Peripheral type is required.');
      return;
    }

    const trimmedName = newTypeName.trim();
    if (!trimmedName) {
      setError('Name is required.');
      return;
    }

    const siteId = Number(siteIdInput);
    if (!Number.isInteger(siteId) || siteId <= 0) {
      setError('Site is required.');
      return;
    }

    setIsSaving(true);
    setError(null);
    setSuccess(null);

    try {
      const trimmedLocation = locationInput.trim();
      const peripheralConfiguration =
        isWeighbridgeCategory || isLoaderScaleCategory
          ? {
              ...(isLoaderScaleCategory ? toLoaderScaleConfig(weighbridgeConfig) : weighbridgeConfig),
              name: trimmedName,
              linkedDevices: linkedDevicesToJson(weighbridgeConfig.linkedDevices)
            }
          : isLfdCategory
            ? { ...lfdConfig, name: trimmedName, linkedDevices: linkedDevicesToJson(lfdConfig.linkedDevices) }
            : isANPRCategory
              ? { ...anprConfig, name: trimmedName, location: trimmedLocation }
              : isRelayCategory
                ? relayConfigToJson({ ...relayConfig, name: trimmedName })
                : { ...(selectedTypeDefaultConfiguration ?? {}), linkedDevices: linkedDevicesToJson(otherLinkedDevices) };

      const configurationJson = peripheralConfiguration == null ? null : JSON.stringify(peripheralConfiguration);

      const payload = {
        peripheralTypeId: selectedPeripheralTypeId,
        peripheralName: trimmedName,
        location: locationInput.trim() ? locationInput.trim() : null,
        notes: notesInput.trim() ? notesInput.trim() : null,
        configuration: configurationJson,
        siteId,
        isActive: true
      };

      if (selectedPeripheralId != null) {
        await updatePeripheral(selectedPeripheralId, payload);
      } else {
        await createPeripheral(payload);
      }

      // revalidatePeripherals re-fetches peripheral types, which gives selectedTypeDefaultConfiguration
      // a new reference and triggers the effect that resets form config. Pinning the ref here
      // causes that effect to restore from the saved values instead of the type defaults.
      if (peripheralConfiguration != null) {
        pendingSelectedConfigRef.current = peripheralConfiguration;
      }

      await Promise.all([revalidatePeripherals(), revalidatePeripheralsBySite()]);
      setSuccess(selectedPeripheralId != null ? `Saved changes to peripheral "${trimmedName}".` : `Saved new peripheral "${trimmedName}".`);
    } catch (createError: any) {
      const message =
        typeof createError?.message === 'string'
          ? createError.message
          : selectedPeripheralId != null
            ? 'Unable to update peripheral.'
            : 'Unable to create peripheral.';
      setError(message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleSelectPeripheral = (peripheral: PeripheralResponse) => {
    setSelectedPeripheralId(peripheral.peripheralId ?? null);
    setSelectedPeripheralTypeId(peripheral.peripheralTypeId);
    setNewTypeName(peripheral.peripheralName ?? '');
    setLocationInput(peripheral.location ?? '');
    setNotesInput(peripheral.notes ?? '');

    if (peripheral.siteId != null) {
      setSiteIdInput(String(peripheral.siteId));
    }

    let parsedConfiguration: unknown = null;
    if (typeof peripheral.configuration === 'string' && peripheral.configuration.trim().length > 0) {
      try {
        parsedConfiguration = JSON.parse(peripheral.configuration);
      } catch {
        parsedConfiguration = null;
      }
    }

    pendingSelectedConfigRef.current = parsedConfiguration;
    // Always set, so a peripheral with no configuration does not show the previous one's links.
    setOtherLinkedDevices(parseLinkedDevices(parsedConfiguration));
    if (parsedConfiguration != null) {
      setWeighbridgeConfig(buildDefaultWeighbridgeConfig(parsedConfiguration));
      setLfdConfig(buildDefaultLfdConfig(parsedConfiguration));
      setAnprConfig(buildDefaultAnprConfig(parsedConfiguration));
      setRelayConfig(buildDefaultRelayConfig(parsedConfiguration));
    }

    setError(null);
    setSuccess(null);
  };

  const handleStartAddPeripheral = () => {
    setSelectedPeripheralId(null);
    setNewTypeName('');
    setLocationInput('');
    setNotesInput('');
    setError(null);
    setSuccess(null);

    if (selectedTypeDefaultConfiguration != null) {
      setWeighbridgeConfig(buildDefaultWeighbridgeConfig(selectedTypeDefaultConfiguration));
      setLfdConfig(buildDefaultLfdConfig(selectedTypeDefaultConfiguration));
      setAnprConfig(buildDefaultAnprConfig(selectedTypeDefaultConfiguration));
      setRelayConfig(buildDefaultRelayConfig(selectedTypeDefaultConfiguration));
    }

    // A new peripheral starts unlinked, rather than inheriting the links of the one selected before it.
    setWeighbridgeConfig((prev) => ({ ...prev, linkedDevices: [] }));
    setLfdConfig((prev) => ({ ...prev, linkedDevices: [], indicatorPeripheralId: null, scaleIndex: null }));
    setRelayConfig((prev) => ({ ...prev, linkedDevices: [], indicatorPeripheralId: null, scaleIndex: null }));
    setOtherLinkedDevices([]);
  };

  const handleSelectCategory = (categoryId: number) => {
    setSelectedCategoryId(categoryId);
    setError(null);
    setSuccess(null);

    const firstPeripheral = peripherals
      .filter((p) => {
        const type = peripheralTypeById.get(p.peripheralTypeId);
        return type?.peripheralCategoryId === categoryId;
      })
      .sort((a, b) => a.peripheralName.localeCompare(b.peripheralName))[0];

    if (firstPeripheral != null) {
      handleSelectPeripheral(firstPeripheral);
    } else {
      setSelectedPeripheralId(null);
      setNewTypeName('');
      setLocationInput('');
      setNotesInput('');
    }
  };

  const handleChangePeripheralStatus = async (peripheral: PeripheralResponse) => {
    if (isSaving || peripheral.peripheralId == null) {
      return;
    }

    setIsSaving(true);
    setError(null);
    setSuccess(null);

    try {
      await updatePeripheralStatus(peripheral.peripheralId, !peripheral.isActive);
      await Promise.all([revalidatePeripherals(), revalidatePeripheralsBySite()]);

      if (selectedPeripheralId === peripheral.peripheralId) {
        handleStartAddPeripheral();
      }

      setSuccess(`Successfully changed peripheral "${peripheral.peripheralName}" to inactive.`);
    } catch (deleteError: any) {
      const message =
        typeof deleteError?.message === 'string' ? deleteError.message : `Unable to change status of ${peripheral.peripheralName}.`;
      setError(message);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Grid container spacing={3}>
      <Grid size={{ xs: 12 }}>
        <Stack spacing={3}>
          <Card>
            <CardContent sx={{ p: { xs: 3, md: 4 } }}>
              <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <TextField
                  select
                  size="small"
                  label="Category"
                  value={selectedCategoryId ?? ''}
                  onChange={(e) => {
                    const id = Number(e.target.value);
                    if (Number.isFinite(id) && id > 0) handleSelectCategory(id);
                  }}
                  disabled={loading || activeCategories.length === 0}
                  sx={{ minWidth: 200 }}
                >
                  {activeCategories.map((category) => (
                    <MenuItem key={category.peripheralCategoryId} value={category.peripheralCategoryId}>
                      {category.peripheralCategoryName}
                    </MenuItem>
                  ))}
                </TextField>
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <TextField
                    select
                    size="small"
                    label="Show"
                    value={peripheralActiveFilter}
                    onChange={(e) => setPeripheralActiveFilter(e.target.value as 'active' | 'inactive' | 'all')}
                    sx={{ minWidth: 120 }}
                  >
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="inactive">Inactive</MenuItem>
                    <MenuItem value="all">All</MenuItem>
                  </TextField>
                  <Button variant="contained" onClick={handleStartAddPeripheral} disabled={isSaving || selectedCategoryId == null}>
                    Add
                  </Button>
                </Stack>
              </Box>

              {loadError ? (
                <Alert severity="error">{loadError}</Alert>
              ) : loading ? (
                <Stack alignItems="center" justifyContent="center" sx={{ minHeight: 140 }}>
                  <CircularProgress size={24} />
                </Stack>
              ) : selectedCategoryId == null ? (
                <Alert severity="info">Select a category to manage peripheral types.</Alert>
              ) : (
                <Stack spacing={1.5}>
                  {selectedSiteId == null ? (
                    <Alert severity="warning">Choose which site you want to work with</Alert>
                  ) : peripheralsError ? (
                    <Alert severity="error">
                      {typeof peripheralsError?.message === 'string' ? peripheralsError.message : 'Unable to load peripherals for site.'}
                    </Alert>
                  ) : peripheralsLoading ? (
                    <Stack alignItems="center" justifyContent="center" sx={{ minHeight: 80 }}>
                      <CircularProgress size={20} />
                    </Stack>
                  ) : categoryPeripherals.length === 0 ? (
                    <Alert severity="info">No peripherals found for this category at this site.</Alert>
                  ) : (
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Type</TableCell>
                            <TableCell>Name</TableCell>
                            <TableCell>Location</TableCell>
                            <TableCell>Notes</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell align="right">Actions</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {categoryPeripherals.map((peripheral) => {
                            const baseTypeName = peripheralTypeById.get(peripheral.peripheralTypeId)?.peripheralTypeName ?? 'Unknown Type';
                            let portConfigType: string | null = null;
                            if (typeof peripheral.configuration === 'string' && peripheral.configuration.trim().length > 0) {
                              try {
                                const parsed = JSON.parse(peripheral.configuration) as Record<string, unknown>;
                                const pc = parsed?.portConfig;
                                if (pc && typeof pc === 'object' && !Array.isArray(pc)) {
                                  const t = (pc as Record<string, unknown>).type;
                                  if (typeof t === 'string') portConfigType = t;
                                }
                              } catch {
                                // ignore malformed JSON
                              }
                            }
                            const typeName = portConfigType != null ? `${baseTypeName} - ${portConfigType}` : baseTypeName;

                            const isSelectedPeripheral =
                              peripheral.peripheralId != null && selectedPeripheralId != null
                                ? peripheral.peripheralId === selectedPeripheralId
                                : false;

                            return (
                              <TableRow
                                key={peripheral.peripheralId ?? `${peripheral.peripheralTypeId}-${peripheral.peripheralName}`}
                                onClick={() => handleSelectPeripheral(peripheral)}
                                sx={{
                                  cursor: 'pointer',
                                  bgcolor: isSelectedPeripheral ? 'primary.light' : 'inherit',
                                  '& .MuiTableCell-root': isSelectedPeripheral ? { color: 'primary.dark' } : {},
                                  '&:hover': {
                                    bgcolor: isSelectedPeripheral ? 'primary.light' : 'grey.50'
                                  }
                                }}
                              >
                                <TableCell>{typeName}</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>{peripheral.peripheralName}</TableCell>
                                <TableCell>{peripheral.location ?? ''}</TableCell>
                                <TableCell>{peripheral.notes ?? ''}</TableCell>
                                <TableCell>
                                  <Chip
                                    label={peripheral.isActive ? 'Active' : 'Inactive'}
                                    color={peripheral.isActive ? 'success' : 'default'}
                                    size="small"
                                    variant="outlined"
                                  />
                                </TableCell>
                                <TableCell align="right">
                                  <Button
                                    color={peripheral.isActive ? 'error' : 'success'}
                                    size="small"
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      void handleChangePeripheralStatus(peripheral);
                                    }}
                                    disabled={isSaving || peripheral.peripheralId == null}
                                  >
                                    {peripheral.isActive ? 'Delete' : 'Recall'}
                                  </Button>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </Stack>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardContent sx={{ p: { xs: 3, md: 4 } }}>
              <Box sx={{ mb: 0.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h4">{selectedPeripheralId != null ? 'Edit Peripheral' : 'Add Peripheral'}</Typography>
                <Button
                  variant="contained"
                  onClick={handleSavePeripheral}
                  disabled={
                    selectedCategoryId == null ||
                    isSaving ||
                    selectedPeripheralTypeId == null ||
                    newTypeName.trim().length === 0 ||
                    siteIdInput.trim().length === 0
                  }
                >
                  {isSaving ? 'Saving...' : 'Save'}
                </Button>
              </Box>
              <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
                {selectedPeripheralId != null
                  ? 'Selected peripheral details are loaded below for editing.'
                  : 'Choose a peripheral type and name, then add a new peripheral to the selected category.'}
              </Typography>

              <Grid container spacing={2} alignItems="center">
                <Grid size={{ xs: 12, md: 4 }}>
                  <TextField
                    fullWidth
                    select
                    label="Peripheral Type"
                    value={selectedPeripheralTypeId ?? ''}
                    onChange={(event) => {
                      const next = Number(event.target.value);
                      setSelectedPeripheralTypeId(Number.isFinite(next) ? next : null);
                    }}
                    disabled={selectedCategoryId == null || isSaving || peripheralTypeOptions.length === 0}
                  >
                    {peripheralTypeOptions.map((type) => (
                      <MenuItem key={type.peripheralTypeId} value={type.peripheralTypeId}>
                        {type.peripheralTypeName}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <TextField
                    fullWidth
                    label="Name"
                    placeholder="Indicator"
                    value={newTypeName}
                    onChange={(event) => setNewTypeName(event.target.value)}
                    disabled={selectedCategoryId == null || isSaving}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <TextField
                    fullWidth
                    label="Location"
                    value={locationInput}
                    onChange={(event) => setLocationInput(event.target.value)}
                    disabled={isSaving}
                  />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    multiline
                    minRows={3}
                    label="Notes"
                    value={notesInput}
                    onChange={(event) => setNotesInput(event.target.value)}
                    disabled={isSaving}
                  />
                </Grid>
              </Grid>

              {isWeighbridgeCategory ? (
                <Box sx={{ mt: 2.5 }}>
                  <WeighbridgePeripheralConfig
                    value={weighbridgeConfig}
                    onChange={setWeighbridgeConfig}
                    peripheralName={newTypeName}
                    onPeripheralNameChange={setNewTypeName}
                    templates={weighbridgeTemplates}
                    disabled={isSaving}
                  />
                </Box>
              ) : isLoaderScaleCategory ? (
                <Box sx={{ mt: 2.5 }}>
                  <LoaderScalePeripheralConfig value={weighbridgeConfig} onChange={setWeighbridgeConfig} disabled={isSaving} />
                </Box>
              ) : isLfdCategory ? (
                <Box sx={{ mt: 2.5 }}>
                  <LFDPeripheralConfig value={lfdConfig} onChange={setLfdConfig} indicators={lfdIndicatorOptions} disabled={isSaving} />
                </Box>
              ) : isANPRCategory ? (
                <Box sx={{ mt: 2.5 }}>
                  <ANPRPeripheralConfig value={anprConfig} onChange={setAnprConfig} disabled={isSaving} />
                </Box>
              ) : isRelayCategory ? (
                <Box sx={{ mt: 2.5 }}>
                  <RelayPeripheralConfig
                    // Remount per peripheral so the advanced JSON text starts from the selected configuration.
                    key={selectedPeripheralId ?? 'new'}
                    value={relayConfig}
                    onChange={setRelayConfig}
                    title={isBarrierArmCategory ? 'Barrier Arm' : 'Traffic Light'}
                    indicators={lfdIndicatorOptions}
                    disabled={isSaving}
                  />
                </Box>
              ) : selectedCategoryId != null ? (
                <Box sx={{ mt: 2.5 }}>
                  <LinkedDevicesConfig value={otherLinkedDevices} onChange={setOtherLinkedDevices} disabled={isSaving} />
                </Box>
              ) : null}

              {error ? (
                <Alert sx={{ mt: 2 }} severity="error">
                  {error}
                </Alert>
              ) : null}
              {success ? (
                <Alert sx={{ mt: 2 }} severity="success">
                  {success}
                </Alert>
              ) : null}
            </CardContent>
          </Card>
        </Stack>
      </Grid>
    </Grid>
  );
}
